import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { AppLoaderService } from '../../../@theme/shared/services/app-loader/app-loader.service';
import { AppNotificationsService } from '../../../core/notifications/app-notifications.service';
import { RecruitmentStepsService } from '../../../settings/recruitment-steps/recruitment-steps.service';
import { EmailComponent } from '../../../shared/email/email.component';
import { EmailEditorDTO, EmptyEmailEditorDTO } from '../../../shared/email/models/api-models';
import { FeedbackRequestsService, SaveFeedbackRequestCDTO } from '../feedback-requests/feedback-requests.service';
import { SabisButton2Component, SabisInput2Component, SabisNgxDialogComponent } from 'sabis-components2';
import { CardViewComponent } from 'src/app/shared/card-view/card-view.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'sca-collect-feedback-request-editor',
  templateUrl: './collect-feedback-request-editor.component.html',
  styleUrls: ['./collect-feedback-request-editor.component.scss'],
  imports: [
    SabisInput2Component,
    SabisButton2Component,
    SabisNgxDialogComponent,
    CardViewComponent,
    EmailComponent,
    FormsModule,
  ]
})
export class CollectFeedbackRequestEditorComponent implements OnInit {
  public title: string = '';
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public isDisabled: boolean = false;

  public userEmail: string = "";
  public sendEmail: boolean = false;
  public email: EmailEditorDTO = EmptyEmailEditorDTO;
  // hide Attach documents button from Email editor if step is FeedbackRequest
  public canAttachDocuments: boolean = false;

  // allowed email template ids (type collect-feedback-request)
  public collectFeedbackEmailTemplateIds: number[] = [];
  @ViewChild('emailForm') emailForm!: EmailComponent;

  constructor(public dialogRef: MatDialogRef<CollectFeedbackRequestEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CollectFeedbackDialogData,
    private _appLoader: AppLoaderService,
    private _appNotification: AppNotificationsService,
    private _collectFeedbackRequestsService: FeedbackRequestsService,
    private _recruitmentStepsService: RecruitmentStepsService

  ) {
    this.title = 'Collect Feedback Request';
    this.emptyEmail();
    this.canAttachDocuments = this.data.canAttachDocuments;
  }

  ngOnInit(): void {
    // get allowed email template ids set up in Settings > Selection Steps > Email Templates
    this._appLoader.showLoading();
    this._recruitmentStepsService.collectFeedbackRequestEmailTemplateIds().subscribe(r => {
      this.collectFeedbackEmailTemplateIds = r;
      this.sendEmail = true;
      this._appLoader.hideLoading();
    });
  }

  private toggleDisabled(): void {
    this.isDisabled = this.isSaving || this.isLoading;
  }

  public onCancelClick(): void {
    this.dialogRef.close();
  }


  public onSaveClick(): void {
    this.save();
  }

  private save(): void {
    if (this.isSaving) {
      return;
    }

    let isValid = this.validateForSave();
    if (!isValid) {
      this._appNotification.completeRequiredFieldMessage();
      return;
    }

    this.isSaving = true;
    this.toggleDisabled();
    this._appLoader.showSaving();
    let command: SaveFeedbackRequestCDTO = {
      userEmail: this.userEmail,
      selectionStepId: this.data.selectionStepId,
      email: null,
      isCollectFeedback: true,
      templateVersionId: this.data.templateVersionId // send selected feedback version from the drop down for the interviewer to fill
    };

    // knowing that sendEmail is set to be checked by default (always true, sending email is always required in order to deliver Collect Feedback request link to interviewer via email)
    if (this.sendEmail) {
      command.email = {
        body: this.email.body,
        emailTemplateId: this.email.emailTemplateId!,
        fromEmail: this.email.fromEmail,
        priorityId: this.email.priorityId!,
        subject: this.email.subject,
        toEmail: this.email.toEmail,
        ccEmail: this.email.ccEmail,
        scheduledDate: this.email.isScheduledForLater == false ? undefined : this.email.scheduledDate,
        isScheduledForLater: this.email.isScheduledForLater,
        attachments: this.email.attachments
      }
    }

    let request = this._collectFeedbackRequestsService.collectFeedbackRequest(this.data.applicationId, command)

    request.pipe(
      finalize(() => {
        this.isSaving = false;
        this.toggleDisabled();
        this._appLoader.hideSaving();
      })
    ).subscribe({
      next: (r) => {
        this._appNotification.notifySaveSucceeded();
        this.dialogRef.close(true);
      },
      error: () => {
        this._appNotification.notifySaveFailed();
      }
    });
  }

  /**
* return true if valid
* @returns
*/
  private validateForSave(): boolean {
    // validate email
    if (this.emailForm && !this.emailForm.validate()) {
      return false;
    }

    if (this.userEmail == "") {
      return false;
    }

    return true;
  }

  public onUserEmailChanged(): void {
    this.email.toEmail = this.userEmail;
  }

  private emptyEmail(): void {
    this.email = { body: "", emailTemplateId: null, fromEmail: "", priorityId: null, subject: "", toEmail: "", ccEmail: "", scheduledDate: undefined, scheduledTime: undefined, isScheduledForLater: false, attachments: [] };
  }
}


export interface CollectFeedbackDialogData {
  templateVersionId: number;
  selectionStepId: number;
  applicationId: number;
  canAttachDocuments: boolean;
}
